/**
 * Telemetry / analytics consent — GDPR baseline (doc 15).
 *
 * Persistent store. Три состояния:
 *   - 'unknown' — игрок ещё не отвечал. emit() работает (буфер), но
 *     remote отправка отложена пока не получим явный 'granted'.
 *   - 'granted' — игрок согласен. emit() + remote (когда backend появится).
 *   - 'denied' — игрок отказался. emit() сразу no-op'ом — нигде не
 *     остаётся следов.
 *
 * Доступно изменить через Settings panel (будет добавлено) или через
 * первый раз через `ConsentBanner` overlay.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { emit } from './emit.js';

export type ConsentStatus = 'unknown' | 'granted' | 'denied';

interface ConsentStore {
  readonly analytics: ConsentStatus;
  /** Когда игрок сделал выбор (для аудита). null если ещё unknown. */
  readonly decidedAt: number | null;
  grant(): void;
  deny(): void;
  /** Test-only reset. */
  _reset(): void;
}

export const useConsentStore = create<ConsentStore>()(
  persist(
    (set, get) => ({
      analytics: 'unknown',
      decidedAt: null,
      grant() {
        const prev = get().analytics;
        set({ analytics: 'granted', decidedAt: Date.now() });
        // Emit только если статус реально изменился (idempotent grant'ы
        // не флудят аналитику). Note: emit самdrop'нется через
        // shouldDropTelemetry() если до этого был 'denied' — но сейчас
        // мы только что переключили на 'granted', так что событие пойдёт.
        if (prev !== 'granted') {
          emit('consent.given', { categories: ['analytics'] });
        }
      },
      deny() {
        const prev = get().analytics;
        // Emit ПЕРЕД set'ом — после set shouldDropTelemetry() вернёт
        // true (status='denied') и emit стал бы no-op. Ловим момент.
        if (prev !== 'denied') {
          emit('consent.withdrawn', { categories: ['analytics'] });
        }
        set({ analytics: 'denied', decidedAt: Date.now() });
      },
      _reset() {
        set({ analytics: 'unknown', decidedAt: null });
      },
    }),
    {
      name: 'sai-consent-v1',
    },
  ),
);

/** Можно ли отправлять remote-телеметрию прямо сейчас? */
export function canSendTelemetry(): boolean {
  return useConsentStore.getState().analytics === 'granted';
}

/** Должен ли emit() полностью пропустить событие (даже из локального буфера)? */
export function shouldDropTelemetry(): boolean {
  return useConsentStore.getState().analytics === 'denied';
}
